import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

export type PatternStatus = 0 | 1; // Inactive=0, Active=1

export interface JobLogPatternDto {
  id: number;
  title: string;
  status: PatternStatus;
  pattern: string;
  category?: string | null;

  linkId?: number | null;
  jobLogType?: number | null;

  fromApplicationId?: number | null;
  toApplicationId?: number | null;
  linkTypeId?: number | null;

  totalMatches: number;
  lastSeenUtc?: string | null;
  createdUtc: string;
  updatedUtc?: string | null;
  description?: string | null;
  freshdeskArticleRowIds?: number[] | null;
  createdByName?: string | null;
  updatedByName?: string | null;
}

export interface UpsertJobLogPatternRequest {
  title: string;
  pattern: string;
  status: PatternStatus;
  category?: string | null;

  linkId?: number | null;
  jobLogType?: number | null;

  fromApplicationId?: number | null;
  toApplicationId?: number | null;
  linkTypeId?: number | null;

  description?: string | null;
  freshdeskArticleIds?: number[] | null;
}

export interface UnmatchedSearchRequest {
	take: number;
	linkId?: number | null;
	jobLogType?: number | null;
	fromApplicationId?: number | null;
	toApplicationId?: number | null;
	fromUtc?: string | null;
	toUtc?: string | null;
	messagePattern?: string | null;
}

export interface UnmatchedGroupDto {
  message: string;
  count: number;
  lastSeenUtc: string;
  jobLogs: UnmatchedJobLogDto[];
}

export interface UnmatchedJobLogDto {
  id: number;
  linkId: number;
  instanceId?: string | null;
}

export interface MatchPatternsRequest {
  message: string;
  linkId?: number | null;
  jobLogType?: number | null;
  fromApplicationId?: number | null;
  toApplicationId?: number | null;
  linkTypeId?: number | null;
  activeOnly?: boolean;
}

export interface MatchPatternsResponse {
  patternIds: number[];
}

export interface JobLogPatternReportRequest {
  fromUtc?: string | null;
  toUtc?: string | null;
  topPatterns?: number;
  topUnmatchedErrors?: number;
  topLinks?: number;
  topApplicationPairs?: number;
  linkId?: number | null;
  fromApplicationId?: number | null;
  toApplicationId?: number | null;
  linkTypeId?: number | null;
  jobLogTypes?: number[] | null;
}

export interface JobLogPatternReportResponse {
  generatedUtc: string;
  currentPeriodStartUtc: string;
  currentPeriodEndUtc: string;
  patternSummary: JobLogPatternReportSummaryDto;
  errorSummary: JobLogPatternErrorCoverageSummaryDto;
  countsByJobLogType: JobLogTypeCountDto[];
  patternsAddedInCurrentPeriod: JobLogPatternWeeklyAdditionDto[];
  topPatterns: JobLogPatternCoverageDto[];
  topUnhandledPatterns: JobLogPatternCoverageDto[];
  topUnmatchedErrors: JobLogPatternUnmatchedErrorDto[];
  dailyTrend: JobLogDailyTrendPointDto[];
  topLinks: JobLogLinkCoverageDto[];
  topApplicationPairs: JobLogApplicationPairCoverageDto[];
  userActivityForCurrentPeriod: JobLogPatternUserWeeklyActivityDto[];
  ownerCoverageForCurrentPeriod: JobLogPatternOwnerWeeklyCoverageDto[];
  categoryCoverageForCurrentPeriod: JobLogPatternCategoryWeeklyCoverageDto[];
  periodComparison?: JobLogPatternPeriodComparisonDto | null;
  allTimeErrorSummary?: JobLogPatternErrorCoverageSummaryDto | null;
  thirtyDaySummary?: JobLogPatternThirtyDayKpiSummaryDto | null;
  topUnmatchedErrorsPeriodComparison?: JobLogPatternUnmatchedErrorPeriodComparisonDto[] | null;
  topCaughtPatternsPeriodComparison?: JobLogPatternPatternPeriodComparisonDto[] | null;
  topUnhandledPatternsPeriodComparison?: JobLogPatternPatternPeriodComparisonDto[] | null;
}

export interface JobLogPatternReportSummaryDto {
  totalPatterns: number;
  activePatterns: number;
  patternsWithArticles: number;
  patternsAppliedToErrors: number;
  handledPatternsAppliedToErrors: number;
  patternsAddedInCurrentPeriod: number;
}

export interface JobLogPatternErrorCoverageSummaryDto {
  totalErrorLogCount: number;
  distinctErrorTypes: number;
  matchedErrorLogCount: number;
  matchedDistinctErrorTypes: number;
  handledErrorLogCount: number;
  handledDistinctErrorTypes: number;
  unassignedMatchedErrorLogCount: number;
  unmatchedErrorLogCount: number;
  unmatchedDistinctErrorTypes: number;
  multiMatchedErrorLogCount: number;
  matchedCoverageRate: number;
  handledCoverageRate: number;
  unmatchedCoverageRate: number;
}

export interface JobLogPatternThirtyDayKpiSummaryDto {
  currentWindowStartUtc: string;
  currentWindowEndUtc: string;
  previousWindowStartUtc: string;
  previousWindowEndUtc: string;
  currentUnmatchedErrorLogCount: number;
  previousUnmatchedErrorLogCount: number;
  unmatchedErrorLogCountDelta: number;
  unmatchedErrorLogCountDeltaRate: number;
  currentPatternsWithArticles: number;
  previousPatternsWithArticles: number;
  patternsWithArticlesDelta: number;
  patternsWithArticlesDeltaRate: number;
  currentTotalPatterns: number;
  previousTotalPatterns: number;
  totalPatternsDelta: number;
  totalPatternsDeltaRate: number;
  currentFilledRate: number;
  previousFilledRate: number;
  filledRateDelta: number;
}

export interface JobLogTypeCountDto {
  jobLogType: number;
  count: number;
}

export interface JobLogPatternWeeklyAdditionDto {
  id: number;
  title: string;
  status: PatternStatus;
  createdUtc: string;
  createdByName?: string | null;
  freshdeskArticleIds: number[];
}

export interface JobLogPatternCoverageDto {
  id: number;
  title: string;
  status: PatternStatus;
  category?: string | null;
  description?: string | null;
  createdUtc: string;
  createdByName?: string | null;
  freshdeskArticleIds: number[];
  totalErrorCount: number;
  distinctErrorTypes: number;
  lastSeenUtc?: string | null;
  jobLogTypes: number[];
}

export interface JobLogPatternUnmatchedErrorDto {
  message: string;
  totalErrorCount: number;
  lastSeenUtc?: string | null;
  jobLogTypes: number[];
}

export interface JobLogPatternUnmatchedErrorPeriodComparisonDto {
  message: string;
  currentPeriodCount: number;
  previousPeriodCount: number;
  deltaCount: number;
  deltaRate: number;
  currentPeriodLastSeenUtc?: string | null;
  previousPeriodLastSeenUtc?: string | null;
}

export interface JobLogPatternPatternPeriodComparisonDto {
  patternId: number;
  title: string;
  category?: string | null;
  hasArticles: boolean;
  currentPeriodMatchedCount: number;
  previousPeriodMatchedCount: number;
  deltaMatchedCount: number;
  deltaMatchedRate: number;
  currentPeriodDistinctErrors: number;
  previousPeriodDistinctErrors: number;
  currentPeriodLastSeenUtc?: string | null;
  previousPeriodLastSeenUtc?: string | null;
}

export interface JobLogDailyTrendPointDto {
  dayUtc: string;
  totalErrorCount: number;
  matchedErrorCount: number;
  handledErrorCount: number;
  unmatchedErrorCount: number;
}

export interface JobLogLinkCoverageDto {
  linkId: number;
  linkName: string;
  companyId: number;
  companyName: string;
  totalErrorCount: number;
  distinctErrorTypes: number;
  matchedErrorCount: number;
  handledErrorCount: number;
  unmatchedErrorCount: number;
  lastSeenUtc?: string | null;
}

export interface JobLogApplicationPairCoverageDto {
  fromApplicationId?: number | null;
  fromApplicationName?: string | null;
  toApplicationId?: number | null;
  toApplicationName?: string | null;
  linkTypeId?: number | null;
  totalErrorCount: number;
  distinctErrorTypes: number;
  matchedErrorCount: number;
  unmatchedErrorCount: number;
  lastSeenUtc?: string | null;
}

export interface JobLogPatternUserWeeklyActivityDto {
  username: string;
  patternsCreatedInCurrentPeriod: number;
  totalPatternsCreatedInCurrentPeriod: number;
  totalPatternsCreatedAllTime: number;
  patternsCreatedDeltaVsAllTime: number;
  patternsCreatedDeltaRateVsAllTime: number;
  matchedErrorsOnCreatedPatternsAllTime: number;
  matchedErrorsOnCreatedPatternsCurrentPeriod: number;
  matchedErrorsOnCreatedPatternsPreviousPeriod: number;
  matchedErrorsDeltaVsPreviousPeriod: number;
  matchedErrorsDeltaRateVsPreviousPeriod: number;
  lastCreatedUtc?: string | null;
  totalPatternsCreatedPreviousPeriod: number;
  patternsCreatedPreviousPeriod: number;
  patternsCreatedDeltaVsPreviousPeriod: number;
  patternsCreatedDeltaRateVsPreviousPeriod: number;
}

export interface JobLogPatternOwnerWeeklyCoverageDto {
  username: string;
  totalPatterns: number;
  activePatterns: number;
  patternsWithArticles: number;
  patternsAppliedToErrors: number;
  handledPatternsAppliedToErrors: number;
  distinctErrorTypes: number;
  totalMatchedErrorCount: number;
  handledMatchedErrorCount: number;
  lastSeenUtc?: string | null;
}

export interface JobLogPatternCategoryWeeklyCoverageDto {
  category: string;
  totalPatterns: number;
  activePatterns: number;
  patternsWithArticles: number;
  patternsAppliedToErrors: number;
  handledPatternsAppliedToErrors: number;
  distinctErrorTypes: number;
  totalMatchedErrorCount: number;
  handledMatchedErrorCount: number;
  lastSeenUtc?: string | null;
}

export interface JobLogPatternPeriodComparisonDto {
  currentPeriodStartUtc: string;
  currentPeriodEndUtc: string;
  previousPeriodStartUtc: string;
  previousPeriodEndUtc: string;
  currentPeriod: JobLogPatternErrorCoverageSummaryDto;
  previousPeriod: JobLogPatternErrorCoverageSummaryDto;
  delta: JobLogPatternPeriodDeltaDto;
}

export interface JobLogPatternPeriodDeltaDto {
  totalErrorLogCountDelta: number;
  matchedErrorLogCountDelta: number;
  handledErrorLogCountDelta: number;
  unmatchedErrorLogCountDelta: number;
  totalErrorLogCountDeltaRate: number;
  matchedErrorLogCountDeltaRate: number;
  handledErrorLogCountDeltaRate: number;
  unmatchedErrorLogCountDeltaRate: number;
  matchedCoverageRateDelta: number;
  handledCoverageRateDelta: number;
  unmatchedCoverageRateDelta: number;
}

export interface JobLogPatternDailySnapshotSummaryDto {
  id: number;
  dayUtc: string;
  generatedUtc: string;
  totalPatterns: number;
  patternsWithArticles: number;
  patternsAddedInCurrentPeriod: number;
  currentPeriodTotalErrorLogCount: number;
  currentPeriodMatchedErrorLogCount: number;
  currentPeriodHandledErrorLogCount: number;
  currentPeriodUnmatchedErrorLogCount: number;
  allTimeTotalErrorLogCount: number;
  allTimeMatchedErrorLogCount: number;
  allTimeHandledErrorLogCount: number;
  allTimeUnmatchedErrorLogCount: number;
  trailingThirtyDayUnmatchedErrorLogCount: number;
  trailingThirtyDayPatternFillRate: number;
  deltaFromPreviousDay?: JobLogPatternDailySnapshotDeltaDto | null;
}

export interface JobLogPatternDailySnapshotDeltaDto {
  currentPeriodTotalErrorLogCountDelta: number;
  currentPeriodMatchedErrorLogCountDelta: number;
  currentPeriodHandledErrorLogCountDelta: number;
  currentPeriodUnmatchedErrorLogCountDelta: number;
  allTimeTotalErrorLogCountDelta: number;
  allTimeMatchedErrorLogCountDelta: number;
  allTimeHandledErrorLogCountDelta: number;
  allTimeUnmatchedErrorLogCountDelta: number;
  trailingThirtyDayUnmatchedErrorLogCountDelta: number;
  totalPatternsDelta: number;
  patternsWithArticlesDelta: number;
  patternsAddedInCurrentPeriodDelta: number;
  trailingThirtyDayPatternFillRateDelta: number;
}

@Injectable({ providedIn: 'root' })
export class JobLogPatternService {
  private url = 'joblogpattern';

  constructor(private http: HttpClient) { }

  list(q?: string, status?: PatternStatus): Observable<JobLogPatternDto[]> {
    let params = new HttpParams();
    if (q?.trim()) params = params.set('q', q.trim());
    if (status !== undefined && status !== null) params = params.set('status', String(status));
    return this.http.get<JobLogPatternDto[]>(this.url, { params });
  }

  create(req: UpsertJobLogPatternRequest): Observable<JobLogPatternDto> {
    return this.http.post<JobLogPatternDto>(this.url, req);
  }

  update(id: number, req: UpsertJobLogPatternRequest): Observable<JobLogPatternDto> {
    return this.http.put<JobLogPatternDto>(`${this.url}/${id}`, req);
  }

  setStatus(id: number, status: PatternStatus): Observable<void> {
    return this.http.patch<void>(`${this.url}/${id}/status`, { status });
  }

  search(req: UnmatchedSearchRequest): Observable<UnmatchedGroupDto[]> {
    return this.http.post<UnmatchedGroupDto[]>(this.url + '/unmatched', req);
  }

  delete(id: number) {
    return this.http.delete<void>(`${this.url}/${id}`);
  }

  matchPatterns(req: MatchPatternsRequest) {
    return this.http.post<MatchPatternsResponse>(`${this.url}/match-patterns`, req);
  }

  refreshArticles(patternId: number) {
    return this.http.post<{
      patternId: number;
      refreshedArticleIds: number[];
      refreshedUtc: string;
    }>(`${this.url}/${patternId}/refresh-articles`, {});
  }

  refreshArticle(patternId: number, articleId: number) {
    return this.http.post<{
      patternId: number;
      refreshedArticleIds: number[];
      refreshedUtc: string;
    }>(`${this.url}/${patternId}/refresh-articles/${articleId}`, {});
  }

  test(req: {
    pattern: string;
    message: string;
    enforceOrder: boolean;
    jobLogType: number | null;
    fromApplicationId: number | null;
    toApplicationId: number | null;
    linkTypeId: number | null;
  }): Observable<{
    isMatch: boolean;
    orderMatch: boolean;
    chunks: string[];
    terms: string[];
    notes?: string | null;
  }> {
    return this.http.post<any>(this.url + '/test', req);
  }

  getReport(req: JobLogPatternReportRequest): Observable<JobLogPatternReportResponse> {
    return this.http.post<JobLogPatternReportResponse>(`${this.url}/report`, req);
  }

  getDailySnapshots(): Observable<JobLogPatternDailySnapshotSummaryDto[]> {
    return this.http.get<JobLogPatternDailySnapshotSummaryDto[]>(`${this.url}/report/daily-snapshots`);
  }
}
